<template>
  <div
    class="border border-gray-100 dark:border-gray-700 rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-700/50"
    :class="{ 'ring-2 ring-blue-500': isSelected }"
  >
    <!-- Collapsible Header -->
    <button
      type="button"
      @click="$emit('toggle')"
      class="w-full text-left p-3 flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
    >
      <ChevronDownIcon
        v-if="isExpanded"
        class="h-4 w-4 text-gray-500 dark:text-gray-400 flex-shrink-0"
      />
      <ChevronRightIcon v-else class="h-4 w-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
      <UserIcon class="h-4 w-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
      <span class="text-sm font-medium text-gray-800 dark:text-gray-200">{{ employeeName }}</span>

      <!-- Employee Info (function, team) -->
      <span
        v-if="employeeInfo"
        class="text-xs text-gray-500 dark:text-gray-400 font-normal truncate"
      >
        {{ employeeInfo }}
      </span>

      <!-- Badge: Selected -->
      <span
        v-if="badge === 'selected'"
        class="ml-auto px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300"
      >
        Geselecteerd
      </span>

      <!-- Badge: Added (comparison employee) -->
      <span v-else-if="badge === 'added'" class="ml-auto text-xs text-gray-500 dark:text-gray-400">
        (toegevoegd)
      </span>

      <!-- Remove Button -->
      <button
        v-if="showRemoveButton"
        @click.stop="$emit('remove')"
        class="ml-auto text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 p-1"
        title="Verwijderen uit vergelijking"
      >
        <XMarkIcon class="h-4 w-4" />
      </button>
    </button>

    <!-- Collapsible Content -->
    <div v-if="isExpanded" class="p-3 pt-0 flex flex-col sm:flex-row gap-3 sm:gap-4">
      <!-- Top/Left: Bar Chart -->
      <div class="flex-1 min-w-0">
        <EmployeeHoursBarChart
          v-if="employee"
          :employee="employee"
          :period-start="periodStart"
          :period-end="periodEnd"
          :potential-dpw="potentialDpw"
          :potential-label="potentialLabel"
          :exclude-proposed-preference-id="excludeProposedPreferenceId"
          unit="hours"
          height="200px"
        />
        <div v-else class="text-sm text-gray-500 dark:text-gray-400 italic py-4 text-center">
          Geen beschikbaarheidsdata beschikbaar
        </div>
      </div>

      <!-- Bottom/Right: Projects & Pre-sourcings Sidebar -->
      <div class="w-full sm:w-64 md:w-80 xl:w-96 2xl:w-[450px] sm:flex-shrink-0">
        <EmployeeProjectsSidebar
          :employee-id="employeeId"
          :period-start="periodStart"
          :period-end="periodEnd"
          :exclude-proposed-preference-id="excludeProposedPreferenceId"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ChevronDownIcon, ChevronRightIcon, UserIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import EmployeeHoursBarChart from '@/components/employee/EmployeeHoursBarChart.vue'
import EmployeeProjectsSidebar from '@/components/employee/EmployeeProjectsSidebar.vue'

defineProps({
  // Required props
  employeeId: {
    type: String,
    required: true
  },
  employeeName: {
    type: String,
    required: true
  },
  employee: {
    type: Object,
    default: null
  },
  periodStart: {
    type: String,
    required: true
  },
  periodEnd: {
    type: String,
    required: true
  },

  // Optional props
  potentialDpw: {
    type: Number,
    default: 0
  },
  potentialLabel: {
    type: String,
    default: 'Dit project'
  },
  excludeProposedPreferenceId: {
    type: [Number, String],
    default: null
  },
  isSelected: {
    type: Boolean,
    default: false
  },
  isExpanded: {
    type: Boolean,
    default: true
  },
  showRemoveButton: {
    type: Boolean,
    default: false
  },
  badge: {
    type: String,
    default: null,
    validator: value => [null, 'selected', 'added'].includes(value)
  },
  employeeInfo: {
    type: String,
    default: ''
  }
})

defineEmits(['toggle', 'remove'])
</script>
